// Service pour calculer les totaux d'heures

import { loadEvents } from './eventService';
import { loadPeriods } from './periodService';
import { getAllSalaires } from './salaireService';

// Ajoutez cette variable pour éviter les calculs multiples rapprochés
let calculDebounceTimer = null;
// Un drapeau pour savoir si un calcul est en cours
let calculInProgress = false;

// Remplacez votre fonction actuelle par cette version optimisée
export async function updateCalculs() {
  // Si un calcul est déjà en cours, ne pas en lancer un autre
  if (calculInProgress) {
    return;
  }

  // Si un timer existe déjà, l'annuler
  if (calculDebounceTimer) {
    clearTimeout(calculDebounceTimer);
  }

  // Définir un nouveau timer pour retarder le calcul
  calculDebounceTimer = setTimeout(async () => {
    try {
      calculInProgress = true;

      console.log('Démarrage du calcul des heures...');

      // Charger les événements et les périodes
      const events = await loadEvents();
      const periods = await loadPeriods();

      // Vérifier si une période est définie
      if (!periods || periods.length === 0) {
        console.warn("Aucune période définie pour les calculs");
        updateCalculDisplay({ total: 0, confirmed: 0, allConfirmed: 0, optionPlus: 0, past: 0 });
        return;
      }

      // Utiliser la première période (on suppose qu'il n'y en a qu'une)
      const currentPeriod = periods[0];
      const periodStart = new Date(currentPeriod.startDate);
      const periodEnd = new Date(currentPeriod.endDate);

      // Filtrer les événements qui sont dans la période
      const eventsInPeriod = events.filter(event => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);

        // Ajuster la date de fin de période pour inclure tout le jour
        const adjustedPeriodEnd = new Date(periodEnd);
        adjustedPeriodEnd.setHours(23, 59, 59, 999);

        // Un événement est dans la période si :
        // - sa date de fin est >= début de période ET
        // - sa date de début est <= fin de période ajustée (pour inclure tout le jour)
        return eventEnd >= periodStart && eventStart <= adjustedPeriodEnd;
      });

      console.log(`Nombre d'événements dans la période (incluant la date de fin): ${eventsInPeriod.length}`);

      // Date du jour pour calculer les événements passés
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let totalHours = 0;
      let confirmedHours = 0;
      let allConfirmedHours = 0; // Ce sera la somme des heures effectuées + heures confirmées futures
      let optionPlusHours = 0;
      let pastHours = 0;
      let confirmedFutureHours = 0; // Heures confirmées futures uniquement

      eventsInPeriod.forEach(event => {
        const hours = parseFloat(event.hours) || 0;
        const eventEndDate = new Date(event.endDate);
        const isPast = eventEndDate < today;

        // Ne pas ajouter les heures des événements "option-" au total général
        if (event.status !== 'option-') {
          totalHours += hours;
        }

        // Calculer les heures passées
        if (isPast) {
          pastHours += hours;
        }

        // Répartir les heures selon le statut
        switch (event.status) {
          case 'ok':
          case 'confirmed':
            if (!isPast) {
              // Heures confirmées futures
              confirmedHours += hours;
              confirmedFutureHours += hours;
            }
            break;
          case 'option+':
            if (!isPast) {
              // Options+ uniquement pour événements futurs
              optionPlusHours += hours;
            } else {
              // Les options+ passées sont traitées comme confirmées automatiquement
              // (et sont déjà comptées dans pastHours)
            }
            break;
          // Les "option-" ne sont pas incluses dans les totaux spécifiques
        }
      });

      // Calculer le total confirmé (effectuées + confirmées futures)
      allConfirmedHours = pastHours + confirmedFutureHours;

      console.log("Heures calculées:", {
        total: totalHours,
        confirmed: confirmedHours,
        allConfirmed: allConfirmedHours,
        optionPlus: optionPlusHours,
        past: pastHours
      });

      // Après avoir filtré eventsInPeriod :
      const allSalaires = await getAllSalaires();

      let totalSalaire = 0;
      let pastSalaire = 0;
      let confirmedSalaire = 0;

      eventsInPeriod.forEach(event => {
        // Exclure les salaires des events "option+"
        if (event.status === 'option+' || event.status === 'option-plus') return;

        const salaireObj = allSalaires.find(s => s.eventId === event.id);
        const salaire = salaireObj ? parseFloat(salaireObj.salaire) || 0 : 0;
        const eventEndDate = new Date(event.endDate);
        const isPast = eventEndDate < today;

        totalSalaire += salaire;
        if (isPast) pastSalaire += salaire;

        // Seuls les événements futurs confirmés sont pris en compte ici
        if (!isPast && (event.status === 'ok' || event.status === 'confirmed')) {
          confirmedSalaire += salaire;
        }
      });

      // Mettre à jour l'affichage
      updateCalculDisplay({
        total: totalHours,
        confirmed: confirmedHours,
        allConfirmed: allConfirmedHours,
        optionPlus: optionPlusHours,
        past: pastHours,
        totalSalaire,
        pastSalaire,
        confirmedSalaire
      });

      console.log('Calcul terminé !');
    } catch (error) {
      console.error('Erreur lors du calcul des heures:', error);
    } finally {
      calculInProgress = false;
      calculDebounceTimer = null;
    }
  }, 300); // Attendre 300ms avant d'exécuter le calcul
}

function updateCalculDisplay(data) {
  // Mise à jour de l'interface utilisateur avec les nouvelles valeurs
  // Utiliser les IDs corrects qui sont présents dans votre HTML
  const totalElement = document.getElementById('total-all-events');
  const confirmedElement = document.getElementById('total-confirmed');
  const allConfirmedElement = document.getElementById('total-all-confirmed'); // Cet élément est déjà dans votre HTML
  const optionPlusElement = document.getElementById('total-options-plus');
  const pastElement = document.getElementById('total-past');

  // Formater les nombres avec une décimale si nécessaire et ajouter "h"
  if (totalElement) totalElement.textContent = formatHours(data.total);
  if (confirmedElement) confirmedElement.textContent = formatHours(data.confirmed);
  if (allConfirmedElement) allConfirmedElement.textContent = formatHours(data.allConfirmed);
  if (optionPlusElement) optionPlusElement.textContent = formatHours(data.optionPlus);
  if (pastElement) pastElement.textContent = formatHours(data.past);

  // Salaires
  const totalSalaireElement = document.getElementById('total-salaire');
  const pastSalaireElement = document.getElementById('past-salaire');
  const confirmedSalaireElement = document.getElementById('confirmed-salaire');
  if (totalSalaireElement) totalSalaireElement.textContent = (data.totalSalaire || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
  if (pastSalaireElement) pastSalaireElement.textContent = (data.pastSalaire || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
  if (confirmedSalaireElement) confirmedSalaireElement.textContent = (data.confirmedSalaire || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
}

// Fonction pour formater les heures (avec 1 décimale si nécessaire)
function formatHours(hours) {
  // Simplement retourner le nombre, avec éventuellement une décimale si nécessaire
  return Number.isInteger(hours) ? hours : hours.toFixed(1);
}